import EventEmitter from "events";
import { Configuration, OpenAIApi } from "openai";
import SSE from "../utils/sse";
import { OpenAIMessage, Parameters } from "./types";
import { backend } from "../backend";
import { buildToolsPayload } from "../../input-tools";
import { HARDCODED_VALUES } from "../../hardcoded-values";

export const defaultModel = 'based1';
export const titleModel = 'kimi-k2-instruct';
export function isProxySupported() {
    return !!backend.current?.services?.includes('openai');
}

function getEndpoint() {
    return '/chatapi/proxies/openai';
}

export interface OpenAIResponseChunk {
    id?: string;
    done: boolean;
    choices?: {
        delta: {
            content: string;
        };
        index: number;
        finish_reason: string | null;
    }[];
    model?: string;
    error?: {
        type: string;
        message: string;
        limit?: number;
        actual?: number;
    };
}

function parseResponseChunk(buffer: any): OpenAIResponseChunk {
    const chunk = buffer.toString().replace('data: ', '').trim();

    if (chunk === '[DONE]') {
        return {
            done: true,
        };
    }

    const parsed = JSON.parse(chunk);

    return {
        id: parsed.id,
        done: false,
        choices: parsed.choices,
        model: parsed.model,
        error: parsed.error,  // Preserve error field
    };
}

export async function createChatCompletion(messages: OpenAIMessage[], parameters: Parameters): Promise<string> {
    const endpoint = getEndpoint();
    
    // Get API token from localStorage
    const apiToken = localStorage.getItem('api_token');
    const headers: any = {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
    };
    
    if (apiToken) {
        headers['X-API-Token'] = apiToken;
    }

    // Build tools payload
    const tools = buildToolsPayload(parameters);
    
    const requestBody: any = {
        "model": parameters.model,
        "messages": messages,
        "temperature": parameters.temperature,
    };
    
    // Add tools if any are active
    if (Object.keys(tools).length > 0) {
        requestBody.tools = tools;
    }

    const response = await fetch(endpoint + '/v1/chat/completions', {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
    });
    
    // Handle token validation errors
    if (response.status === 401) {
        const errorData = await response.json();
        throw new Error(JSON.stringify({ 
            message: `Your session token has expired. Please refresh the page to get a new token and try again.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`, 
            type: 'TOKEN_EXPIRED' 
        }));
    }
    
    if (response.status === 403) {
        const errorData = await response.json();
        if (errorData?.error === 'session_validation_required') {
            console.log('[OpenAI] Session validation required, triggering refresh...');
            // Trigger session refresh through backend
            if (backend.current) {
                await backend.current.getSession();
                // Retry with new token
                const newToken = localStorage.getItem('api_token');
                if (newToken) {
                    headers['X-API-Token'] = newToken;
                    const retryResponse = await fetch(endpoint + '/v1/chat/completions', {
                        method: "POST",
                        headers,
                        body: JSON.stringify({
                            "model": parameters.model,
                            "messages": messages,
                            "temperature": parameters.temperature,
                        }),
                    });
                    if (retryResponse.ok) {
                        const data = await retryResponse.json();
                        return data.choices[0].message?.content?.trim() || '';
                    }
                }
            }
        }
        throw new Error((errorData?.message || 'Session Unauthorized') + `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`);
    }
    
    // Handle server errors
    if (response.status === 500) {
        throw new Error(`Internal server error. Please try again later.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`);
    }
    
    if (response.status === 501) {
        throw new Error(`Service temporarily unavailable. Please try again later.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`);
    }

    const data = await response.json();

    return data.choices[0].message?.content?.trim() || '';
}

export async function createStreamingChatCompletion(messages: OpenAIMessage[], parameters: Parameters) {
    const emitter = new EventEmitter();
    const endpoint = getEndpoint();
    
    // Get API token from localStorage
    const apiToken = localStorage.getItem('api_token');
    const headers: any = {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
    };
    
    if (apiToken) {
        headers['X-API-Token'] = apiToken;
    }
    
    // Enhanced Safari/iOS/Mac detection
    const userAgent = navigator.userAgent.toLowerCase();
    const isMac = userAgent.includes('macintosh') || userAgent.includes('mac os x');
    const isIOS = /ipad|iphone|ipod/.test(userAgent) || (isMac && 'ontouchend' in document);
    const isSafari = (
        (userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('chromium')) ||
        isIOS ||
        (isMac && userAgent.includes('version/'))
    );
    
    console.log('[Browser Detection] User Agent:', navigator.userAgent);
    console.log('[Browser Detection] Is Mac:', isMac, 'Is iOS:', isIOS, 'Is Safari:', isSafari);
    
    if (isSafari) {
        console.log('[Safari Compatibility] Using fetch-based streaming fallback for Safari/iOS');
        // Use fetch-based streaming for Safari instead of SSE
        return createSafariStreamingFallback(messages, parameters, headers, endpoint, emitter);
    } else {
        console.log('[Browser Compatibility] Using standard SSE implementation');
    }

    // Build tools payload
    const tools = buildToolsPayload(parameters);
    
    const requestBody: any = {
        "model": parameters.model,
        "messages": messages,
        "temperature": parameters.temperature,
        "stream": true,
        "personality": parameters.personality,
        "extreme": parameters.extreme,
    };
    
    // Add tools if any are active
    if (Object.keys(tools).length > 0) {
        requestBody.tools = tools;
    }

    const eventSource = new SSE(endpoint + '/v1/chat/completions', {
        method: "POST",
        headers,
        payload: JSON.stringify(requestBody),
    }) as SSE;

    let contents = '';
    let lastFinishReason: string | null = null;

    eventSource.addEventListener('error', async (event: any) => {
        if (!contents) {
            let error = event.data;
            let errorType = 'GENERIC_ERROR';
            console.log('[DEBUG] SSE Error received:', event.data);
            try {
                const parsed = JSON.parse(error);
                console.log('[DEBUG] Parsed error:', parsed);
                error = parsed.error?.message || parsed.message;
                errorType = parsed.error || 'GENERIC_ERROR';
                
                // Handle token expired error (401 - completely invalid token)
                if (parsed.error === 'Unauthorized') {
                    emitter.emit('error', { 
                        message: `Your session token has expired. Please refresh the page to get a new token and try again.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`, 
                        type: 'TOKEN_EXPIRED' 
                    });
                    return;
                }
                
                // Handle session validation required error
                if (parsed.error === 'session_validation_required') {
                    console.log('[OpenAI Streaming] Session validation required, triggering refresh...');
                    if (backend.current) {
                        await backend.current.getSession();
                        // Emit special error to trigger retry
                        emitter.emit('error', { message: `Session expired, please retry.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`, type: 'SESSION_EXPIRED' });
                        return;
                    }
                }
                
                // Handle character limit error
                if (parsed.error?.type === 'character_limit') {
                    const { limit, actual } = parsed.error;
                    emitter.emit('error', { 
                        message: `Your message is too long (${actual} characters). Please shorten it to ${limit} characters or less.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`, 
                        type: 'CHARACTER_LIMIT',
                        limit,
                        actual
                    });
                    return;
                }
                
                // Handle tool execution failures
                if (parsed.error?.type === 'tool_failed') {
                    emitter.emit('error', { 
                        message: parsed.error.message,
                        type: 'TOOL_FAILED'
                    });
                    return;
                }
                
                // Handle external API errors
                if (parsed.error?.type === 'api_error') {
                    emitter.emit('error', { 
                        message: parsed.error.message,
                        type: 'API_ERROR'
                    });
                    return;
                }
                
                // Handle timeout errors
                if (parsed.error?.type === 'timeout') {
                    emitter.emit('error', { 
                        message: parsed.error.message,
                        type: 'TIMEOUT'
                    });
                    return;
                }
                
                // Handle content moderation error
                if (parsed.error === 'CONTENT_MODERATION') {
                    if (parsed.action === 'SHOW_FLAGGED_MESSAGE') {
                        emitter.emit('error', { 
                            message: (parsed.message || 'Your message was blocked due to content policy violations') + `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`,
                            type: 'CONTENT_MODERATION',
                            action: 'SHOW_FLAGGED_MESSAGE',
                            flaggedContent: parsed.flaggedContent,
                            violatedCategories: parsed.violatedCategories
                        });
                        return;
                    } else if (parsed.action === 'REMOVE_LAST_MESSAGE') {
                        emitter.emit('error', { 
                            message: (parsed.message || 'Your message was blocked due to content policy violations') + `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`,
                            type: 'CONTENT_MODERATION',
                            removeLastMessage: true
                        });
                        return;
                    }
                }
            } catch (e) {}
            emitter.emit('error', { message: error + `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`, type: errorType });
        }
    });

    eventSource.addEventListener('message', async (event: any) => {
        if (event.data === '[DONE]') {
            emitter.emit('done');
            return;
        }

        try {
            const chunk = parseResponseChunk(event.data);
            
            // Check if this is an error response
            if (chunk.error) {
                const errorObj = chunk.error;
                
                // Handle tool execution failures
                if (errorObj.type === 'tool_failed') {
                    emitter.emit('error', { 
                        message: errorObj.message,
                        type: 'TOOL_FAILED'
                    });
                    eventSource.close();
                    return;
                }
                
                // Handle external API errors
                if (errorObj.type === 'api_error') {
                    emitter.emit('error', { 
                        message: errorObj.message,
                        type: 'API_ERROR'
                    });
                    eventSource.close();
                    return;
                }
                
                // Handle timeout errors
                if (errorObj.type === 'timeout') {
                    emitter.emit('error', { 
                        message: errorObj.message,
                        type: 'TIMEOUT'
                    });
                    eventSource.close();
                    return;
                }
                
                // Handle character limit errors
                if (errorObj.type === 'character_limit') {
                    emitter.emit('error', { 
                        message: `Your message is too long (${errorObj.actual} characters). Please shorten it to ${errorObj.limit} characters or less.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`,
                        type: 'CHARACTER_LIMIT',
                        limit: errorObj.limit,
                        actual: errorObj.actual
                    });
                    eventSource.close();
                    return;
                }
            }
            
            if (chunk.choices && chunk.choices.length > 0) {
                const choice = chunk.choices[0];
                const deltaContent = choice?.delta?.content || '';
                
                // If we're transitioning from a tool call to new content, add a space
                if (lastFinishReason === 'tool_calls' && deltaContent && contents.length > 0) {
                    contents += ' ';
                    lastFinishReason = null; // Reset so we don't keep adding spaces
                }
                
                contents += deltaContent;
                emitter.emit('data', contents);
                
                // Track finish reason for next chunk
                if (choice?.finish_reason) {
                    lastFinishReason = choice.finish_reason;
                }
            }
        } catch (e) {
            console.error(e);
        }
    });

    eventSource.stream();

    return {
        emitter,
        cancel: () => eventSource.close(),
    };
}

// Safari-compatible streaming fallback using fetch
async function createSafariStreamingFallback(messages: OpenAIMessage[], parameters: Parameters, headers: any, endpoint: string, emitter: EventEmitter) {
    let cancelled = false;
    let response: Response | null = null;
    
    const cancel = () => {
        cancelled = true;
        if (response && response.body) {
            try {
                response.body.cancel();
            } catch (e) {
                console.warn('Failed to cancel Safari stream:', e);
            }
        }
    };
    
    try {
        // Build tools payload
        const tools = buildToolsPayload(parameters);
        
        const requestBody: any = {
            "model": parameters.model,
            "messages": messages,
            "temperature": parameters.temperature,
            "stream": true,
            "personality": parameters.personality,
            "extreme": parameters.extreme,
        };
        
        // Add tools if any are active
        if (Object.keys(tools).length > 0) {
            requestBody.tools = tools;
        }
        
        response = await fetch(endpoint + '/v1/chat/completions', {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            let errorData;
            try {
                errorData = JSON.parse(errorText);
            } catch (e) {
                errorData = { error: errorText };
            }
            
            // Handle various error types similar to SSE handler
            if (response.status === 401) {
                emitter.emit('error', { 
                    message: `Your session token has expired. Please refresh the page to get a new token and try again.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`, 
                    type: 'TOKEN_EXPIRED' 
                });
                return { emitter, cancel };
            }
            
            if (response.status === 403 && errorData?.error === 'session_validation_required') {
                console.log('[Safari Streaming] Session validation required, triggering refresh...');
                if (backend.current) {
                    await backend.current.getSession();
                    emitter.emit('error', { message: `Session expired, please retry.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`, type: 'SESSION_EXPIRED' });
                    return { emitter, cancel };
                }
            }
            
            // Handle specific error types from server
            if (errorData?.error?.type === 'tool_failed') {
                emitter.emit('error', { 
                    message: errorData.error.message,
                    type: 'TOOL_FAILED'
                });
                return { emitter, cancel };
            }
            
            if (errorData?.error?.type === 'api_error') {
                emitter.emit('error', { 
                    message: errorData.error.message,
                    type: 'API_ERROR'
                });
                return { emitter, cancel };
            }
            
            if (errorData?.error?.type === 'timeout') {
                emitter.emit('error', { 
                    message: errorData.error.message,
                    type: 'TIMEOUT'
                });
                return { emitter, cancel };
            }
            
            // Handle server errors
            if (response.status === 500) {
                emitter.emit('error', { message: `Internal server error. Please try again later.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!` });
                return { emitter, cancel };
            }
            
            if (response.status === 501) {
                emitter.emit('error', { message: `Service temporarily unavailable. Please try again later.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!` });
                return { emitter, cancel };
            }
            
            // Add Discord link to other errors
            const errorMessage = typeof errorData === 'object' && errorData.message 
                ? errorData.message + `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!` 
                : (errorData || 'Unknown error') + `\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`;
            emitter.emit('error', { message: errorMessage });
            return { emitter, cancel };
        }
        
        if (!response.body) {
            console.error('[Safari Streaming] No response body received');
            emitter.emit('error', 'No response body received from server');
            return { emitter, cancel };
        }
        
        console.log('[Safari Streaming] Successfully got response body, starting stream processing...');
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let contents = '';
        let buffer = '';
        let lastFinishReason: string | null = null;
        
        const processChunk = async () => {
            if (cancelled) return;
            
            try {
                const { done, value } = await reader.read();
                
                if (done) {
                    emitter.emit('done');
                    return;
                }
                
                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;
                
                // Process complete SSE messages - handle both \n\n and \r\n\r\n for cross-platform compatibility
                const lines = buffer.split(/\r?\n\r?\n/);
                buffer = lines.pop() || ''; // Keep incomplete message in buffer
                
                for (const line of lines) {
                    if (cancelled) return;
                    
                    const trimmedLine = line.trim();
                    if (trimmedLine === 'data: [DONE]' || trimmedLine === '[DONE]') {
                        emitter.emit('done');
                        return;
                    }
                    
                    if (trimmedLine.startsWith('data: ')) {
                        const data = trimmedLine.slice(6);
                        if (data.trim()) {
                            try {
                                const parsed = JSON.parse(data);
                                
                                // Check if this is an error response
                                if (parsed.error) {
                                    const errorObj = parsed.error;
                                    
                                    // Handle tool execution failures
                                    if (errorObj.type === 'tool_failed') {
                                        emitter.emit('error', { 
                                            message: errorObj.message,
                                            type: 'TOOL_FAILED'
                                        });
                                        cancelled = true;  // Stop processing
                                        try { reader.cancel(); } catch {}
                                        return;
                                    }
                                    
                                    // Handle external API errors
                                    if (errorObj.type === 'api_error') {
                                        emitter.emit('error', { 
                                            message: errorObj.message,
                                            type: 'API_ERROR'
                                        });
                                        cancelled = true;  // Stop processing
                                        try { reader.cancel(); } catch {}
                                        return;
                                    }
                                    
                                    // Handle timeout errors
                                    if (errorObj.type === 'timeout') {
                                        emitter.emit('error', { 
                                            message: errorObj.message,
                                            type: 'TIMEOUT'
                                        });
                                        cancelled = true;  // Stop processing
                                        try { reader.cancel(); } catch {}
                                        return;
                                    }
                                    
                                    // Handle character limit errors
                                    if (errorObj.type === 'character_limit') {
                                        emitter.emit('error', { 
                                            message: `Your message is too long (${errorObj.actual} characters). Please shorten it to ${errorObj.limit} characters or less.\n\n💬 Need help? Join our [Discord](${HARDCODED_VALUES.discord.inviteLink}) community!`,
                                            type: 'CHARACTER_LIMIT',
                                            limit: errorObj.limit,
                                            actual: errorObj.actual
                                        });
                                        cancelled = true;  // Stop processing
                                        try { reader.cancel(); } catch {}
                                        return;
                                    }
                                }
                                
                                if (parsed.choices && parsed.choices.length > 0) {
                                    const choice = parsed.choices[0];
                                    const deltaContent = choice?.delta?.content || '';
                                    
                                    // If we're transitioning from a tool call to new content, add a space
                                    if (lastFinishReason === 'tool_calls' && deltaContent && contents.length > 0) {
                                        contents += ' ';
                                        lastFinishReason = null; // Reset so we don't keep adding spaces
                                    }
                                    
                                    contents += deltaContent;
                                    emitter.emit('data', contents);
                                    
                                    // Track finish reason for next chunk
                                    if (choice?.finish_reason) {
                                        lastFinishReason = choice.finish_reason;
                                    }
                                }
                            } catch (e) {
                                console.warn('[Safari Streaming] Parse error for chunk:', data.slice(0, 100), e);
                                // Continue processing other chunks
                            }
                        }
                    }
                }
                
                // Continue processing with small delay for Safari compatibility
                if (!cancelled) {
                    setTimeout(processChunk, 1);
                }
                
            } catch (error) {
                if (!cancelled) {
                    console.error('[Safari Streaming] Stream processing error:', error);
                    emitter.emit('error', error instanceof Error ? error.message : String(error));
                }
            }
        };
        
        // Start processing
        processChunk();
        
    } catch (error) {
        console.error('[Safari Streaming] Request failed:', error);
        emitter.emit('error', error instanceof Error ? error.message : String(error));
    }
    
    return { emitter, cancel };
}

export const maxTokensByModel = {
    "gpt-3.5-turbo": 4096,
    "gpt-4": 8192,
    "gpt-4-0613": 8192,
    "gpt-4-32k": 32768,
    "gpt-4-32k-0613": 32768,
    "gpt-3.5-turbo-16k": 16384,
    "gpt-3.5-turbo-0613": 4096,
    "gpt-3.5-turbo-16k-0613": 16384,
};
